import { useEffect, useRef, useState } from 'react';
import { discardItem, onDelivery, retryAll, useOutbox, type OutboxItem } from '../lib/outbox';
import { colors } from '../theme';

// Session-scoped delivered flash, identical to app/src/components/OutboxPill.tsx.
// Items pre-dating this mount (timestamped from before Date.now() at mount)
// won't trigger the "Delivered" pulse.
export function OutboxPill() {
  const { items, pending, sending, failed, awaitingMeta } = useOutbox();
  const [open, setOpen] = useState(false);
  const [flash, setFlash] = useState(false);
  const sessionIdsRef = useRef<Set<string>>(new Set());
  const mountedAtRef = useRef<number>(Date.now());

  useEffect(() => {
    for (const it of items) {
      const stamp = parseInt(it.id.split('-')[0] ?? '0', 10);
      if (stamp >= mountedAtRef.current) sessionIdsRef.current.add(it.id);
    }
  }, [items]);

  useEffect(() => {
    return onDelivery(({ id }) => {
      if (!sessionIdsRef.current.has(id)) return;
      sessionIdsRef.current.delete(id);
      setFlash(true);
      const t = setTimeout(() => setFlash(false), 1400);
      return () => clearTimeout(t);
    });
  }, []);

  const retrying = items.some((i) => i.attempts >= 2 && i.state !== 'sending');
  const total = pending + sending + failed + awaitingMeta;
  if (total === 0 && !flash) return null;

  let label: string;
  let tone: string;
  if (flash && total === 0) {
    label = 'Delivered';
    tone = colors.alt;
  } else if (failed > 0) {
    const queued = pending + sending + awaitingMeta;
    label = `${failed} failed${queued > 0 ? ` · ${queued} queued` : ''}`;
    tone = colors.danger;
  } else if (retrying && total > 0) {
    label = `${total} queued — retrying`;
    tone = colors.accentDim;
  } else if (sending > 0 || awaitingMeta > 0) {
    label = total > 1 ? `Sending ${total}…` : 'Sending…';
    tone = colors.accent;
  } else {
    label = total > 1 ? `${total} queued` : 'Queued';
    tone = colors.accent;
  }

  return (
    <>
      <button
        className={`outbox-pill ${flash && total === 0 ? 'outbox-flash' : ''}`}
        onClick={() => total > 0 && setOpen(true)}
        style={{ color: tone, borderColor: tone }}
      >
        {label}
      </button>

      {open && (
        <div className="outbox-sheet" onClick={(e) => e.target === e.currentTarget && setOpen(false)}>
          <div className="outbox-inner">
            <div className="outbox-header">
              <h3>Outgoing</h3>
              <button onClick={() => setOpen(false)} className="outbox-done">
                Done
              </button>
            </div>
            {retrying && (
              <p className="outbox-note">
                Retrying in the background. If signal is weak, these will send when you reconnect.
              </p>
            )}
            {failed > 0 && (
              <button className="outbox-retry-all" onClick={() => retryAll()}>
                Retry all failed
              </button>
            )}
            <ul className="outbox-list">
              {items.map((it) => (
                <OutboxRow key={it.id} item={it} />
              ))}
            </ul>
          </div>
        </div>
      )}

      <style>{`
        .outbox-pill {
          position: absolute;
          bottom: calc(env(safe-area-inset-bottom) + 132px);
          left: 50%;
          transform: translateX(-50%);
          padding: 6px 14px;
          border-radius: 999px;
          font-size: 13px;
          font-weight: 600;
          background: rgba(23,22,28,0.92);
          border: 1px solid;
          z-index: 1500;
        }
        .outbox-flash {
          background: rgba(125,138,106,0.18);
        }
        .outbox-sheet {
          position: fixed;
          inset: 0;
          background: rgba(0,0,0,0.6);
          z-index: 2000;
          display: flex;
          align-items: flex-end;
        }
        .outbox-inner {
          width: 100%;
          background: ${colors.bg};
          border-radius: 16px 16px 0 0;
          padding: 16px;
          max-height: 80vh;
          overflow-y: auto;
        }
        .outbox-header {
          display: flex;
          justify-content: space-between;
          align-items: center;
          padding-bottom: 10px;
          border-bottom: 1px solid ${colors.divider};
        }
        .outbox-header h3 { margin: 0; color: ${colors.text}; font-size: 18px; }
        .outbox-done {
          background: transparent;
          color: ${colors.accent};
          border: none;
          font-size: 14px;
          font-weight: 600;
        }
        .outbox-note {
          margin: 12px 0;
          padding: 10px;
          border-radius: 8px;
          background: ${colors.surface};
          border-left: 3px solid ${colors.accentDim};
          color: ${colors.textMuted};
          font-size: 12px;
        }
        .outbox-retry-all {
          margin: 12px 0;
          padding: 12px;
          background: ${colors.accent};
          color: ${colors.bg};
          border: none;
          border-radius: 10px;
          width: 100%;
          font-weight: 700;
        }
        .outbox-list {
          list-style: none;
          padding: 0;
          margin: 10px 0 0;
          display: flex;
          flex-direction: column;
          gap: 8px;
        }
      `}</style>
    </>
  );
}

function OutboxRow({ item }: { item: OutboxItem }) {
  const preview = item.transcript?.trim() || '🎤 voice message';
  const dur = Math.round((item.duration_ms ?? 0) / 1000);
  let badge: string;
  let badgeColor: string;
  if (item.state === 'failed') {
    badge = 'Failed';
    badgeColor = colors.danger;
  } else if (item.state === 'sending') {
    badge = item.uploaded_path ? 'Delivering…' : 'Uploading…';
    badgeColor = colors.accent;
  } else if (!item.finalized) {
    badge = item.uploaded_path ? 'Waiting on transcript' : 'Uploading (awaiting transcript)';
    badgeColor = colors.textMuted;
  } else {
    badge = `Queued · try ${item.attempts + 1}`;
    badgeColor = colors.textMuted;
  }
  return (
    <li className="outbox-row">
      <div className="outbox-row-body">
        <div className="outbox-preview">{preview}</div>
        <div className="outbox-meta">
          {dur}s · <span style={{ color: badgeColor }}>{badge}</span>
          {item.last_error ? ` · ${truncate(item.last_error, 60)}` : ''}
        </div>
      </div>
      {item.state === 'failed' && (
        <button className="outbox-discard" onClick={() => discardItem(item.id)}>
          Discard
        </button>
      )}
      <style>{`
        .outbox-row {
          display: flex;
          align-items: center;
          gap: 12px;
          background: ${colors.surface};
          border-radius: 10px;
          padding: 12px;
        }
        .outbox-row-body { flex: 1; min-width: 0; }
        .outbox-preview {
          color: ${colors.text};
          font-size: 14px;
          white-space: nowrap;
          overflow: hidden;
          text-overflow: ellipsis;
        }
        .outbox-meta { color: ${colors.textMuted}; font-size: 11px; margin-top: 4px; }
        .outbox-discard {
          background: transparent;
          color: ${colors.danger};
          border: none;
          font-size: 12px;
          font-weight: 600;
        }
      `}</style>
    </li>
  );
}

function truncate(s: string, n: number) {
  return s.length > n ? s.slice(0, n - 1) + '…' : s;
}
